import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  Truck, MapPin, DollarSign, Fuel, Upload, 
  CheckCircle, AlertTriangle, FileText, ArrowRight, ExternalLink
} from 'lucide-react';
import { cn } from '@/lib/utils';
import LoadChat from '../components/LoadChat';
import FuelAdvanceModal from '../components/FuelAdvanceModal';
import LoadReceiptUpload from '../components/LoadReceiptUpload';
import { useRealtimeLoads } from '@/hooks/use-realtime-loads';
import { calcRoute, bestStop } from '@/lib/pcmiler';
import { computeLoadGross, nextInvoiceNumber, computePayDueAt, isSameCalendarWeek, numOrDefault } from '@/lib/invoiceHelpers';
import { findMyDriverProfile } from '@/lib/myDriverProfile';
import PendingApprovalNotice from '@/components/PendingApprovalNotice';
import { startLocationWatch, isNativePlatform } from '@/lib/backgroundLocation';

const statusColors = {
  'Booked': 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30',
  'In Transit': 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  'Delivered': 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30',
};

const statusSteps = ['Booked', 'In Transit', 'Delivered'];

export default function ActiveLoad() {
  const { user, carrierId } = useAuth();
  const [load, setLoad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [requestingAdvance, setRequestingAdvance] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [requestingPersonalAdvance, setRequestingPersonalAdvance] = useState(false);
  const [showPersonalAdvanceModal, setShowPersonalAdvanceModal] = useState(false);
  const [advances, setAdvances] = useState([]);
  const [personalAdvances, setPersonalAdvances] = useState([]);
  // Sum of Expense rows (fuel/toll/scale/repair receipts) the driver has
  // logged against this specific load -- offsets the approved fuel advance
  // below so a driver who documents where the money went isn't charged for
  // it. Kept as live component state (not re-fetched inside the preview
  // IIFE) so LoadReceiptUpload's own fetch is the single source of truth.
  const [documentedTotal, setDocumentedTotal] = useState(0);
  const [driverProfile, setDriverProfile] = useState(null);
  const [companySettings, setCompanySettings] = useState([]);
  const [deliveringLoad, setDeliveringLoad] = useState(false);
  // 'idle' (waiting on a first fix) | 'active' (pings are going out) |
  // 'denied' | 'unavailable' | 'unsupported' -- surfaced in the UI below
  // so a driver whose location sharing silently fails isn't left guessing
  // why dispatch can't see them, instead of failing with zero feedback.
  const [locationStatus, setLocationStatus] = useState('idle');

  useEffect(() => {
    loadData();
  }, [user]);

  // silent: true for the realtime-triggered background refresh below --
  // without it, ANY load change anywhere for the carrier (not just this
  // driver's own active load) flips `loading` back to true, which
  // unmounts the whole page tree (the `if (loading) return <spinner>`
  // below) and wipes any in-progress local state in it -- confirmed the
  // hard way: a driver's pending "confirm receipt details" form
  // (LoadReceiptUpload's own local state) silently vanished mid-review
  // because some unrelated load elsewhere changed status at that exact
  // moment. The explicit calls elsewhere in this file (initial mount,
  // after marking Delivered) still want the spinner, so they're
  // unaffected -- only the realtime subscription passes silent.
  const loadData = async ({ silent = false } = {}) => {
    if (!user?.email) return;
    if (!silent) setLoading(true);
    // SECURITY: Filter loads strictly by the logged-in driver's email (case-insensitive match)
    const allLoads = await client.entities.Load.list('-updated_date', 200);
    const loads = allLoads.filter(l =>
      l.assigned_driver_email?.toLowerCase() === user.email.toLowerCase()
    );
    const active = loads.find(l => l.status === 'Booked' || l.status === 'In Transit');
    // Only reset while switching to a different (or no) load -- LoadReceiptUpload
    // only re-fetches its own total when load.id changes (see its useEffect),
    // so resetting on every silent background refresh of the SAME load would
    // leave this stuck at 0 with nothing left to correct it back.
    if (active?.id !== load?.id) setDocumentedTotal(0);
    setLoad(active || null);

    if (active) {
      // SECURITY: Scope fuel advances to the specific load AND driver
      const fuelAdvances = await client.entities.FuelAdvance.filter({
        load_id: active.id,
        driver_email: user.email,
      });
      setAdvances(fuelAdvances);
    }

    // Personal advances aren't tied to a specific load (they're a weekly
    // cash-flow request, not deducted from any one load's proceeds -- see
    // PayrollTab's payroll-time deduction), so this is scoped by driver
    // only, independent of whether there's an active load.
    const personal = await client.entities.FuelAdvance.filter({
      driver_email: user.email,
      advance_type: 'personal',
    });
    setPersonalAdvances(personal);

    const profile = await findMyDriverProfile(user.email);
    setDriverProfile(profile);

    // Carrier Take % / Dispatch Fee % -- fetched once here and reused
    // by both the live Load Breakdown preview below and
    // markLoadDelivered's actual Settlement math, so the number a
    // driver sees before delivering never disagrees with what they're
    // actually charged.
    const settings = await client.entities.CompanySettings.list().catch(() => []);
    setCompanySettings(settings);

    setLoading(false);
  };

  useRealtimeLoads(carrierId, () => loadData({ silent: true }));

  // GPS breadcrumb pings while this load is In Transit. On the web/PWA
  // build this is foreground-only (stops the instant the tab loses focus
  // or the load leaves In Transit) via navigator.geolocation. On a native
  // app build (Capacitor), true background tracking is available via
  // @capacitor-community/background-geolocation -- but that requires the
  // driver to explicitly opt in first (nativeTrackingEnabled below), since
  // starting it is also the call that triggers the OS's "Always Allow"
  // location prompt, and both Apple and Google expect an in-app
  // explanation before that dialog fires, not an automatic request on
  // mount. See src/lib/backgroundLocation.js and PHASE0_DECISIONS.md's P2-4.
  const [nativeTrackingEnabled, setNativeTrackingEnabled] = useState(false);
  useEffect(() => {
    if (!load?.id || load.status !== 'In Transit') return;
    if (!load?.id || load.status !== 'In Transit') return;

    // Native app build (Capacitor): true background tracking via
    // @capacitor-community/background-geolocation, gated behind the
    // driver's explicit opt-in (nativeTrackingEnabled) since starting the
    // watcher is also what triggers the OS's "Always Allow" location
    // prompt -- see the opt-in banner below and src/lib/backgroundLocation.js.
    if (isNativePlatform()) {
      if (!nativeTrackingEnabled) { setLocationStatus('idle'); return; }
      let lastWrite = 0;
      let stopWatch = null;
      let cancelled = false;
      startLocationWatch({
        onLocation: (lat, lng) => {
          setLocationStatus('active');
          const now = Date.now();
          if (now - lastWrite < 60000) return; // throttle to one ping/60s
          lastWrite = now;
          client.entities.LoadLocationPing.create({
            load_id: load.id,
            driver_email: user.email,
            lat,
            lng,
          }).catch(() => { /* non-blocking -- a missed ping shouldn't disrupt the drive */ });
        },
        onError: () => { setLocationStatus('denied'); },
      }).then((stop) => {
        if (cancelled) stop();
        else stopWatch = stop;
      });
      return () => {
        cancelled = true;
        stopWatch?.();
      };
    }

    // Web/PWA: getCurrentPosition on a fixed timer, NOT watchPosition --
    // watchPosition only re-fires when the browser's location provider
    // decides the position changed "enough", which for a stationary or
    // slow-moving truck (or a backgrounded/throttled tab) can be far less
    // often than every 5 minutes, or not at all. A plain interval
    // guarantees the actual cadence dispatch sees.
    if (!navigator.geolocation) { setLocationStatus('unsupported'); return; }
    setLocationStatus('idle');

    const writePing = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocationStatus('active');
          client.entities.LoadLocationPing.create({
            load_id: load.id,
            driver_email: user.email,
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          }).catch(() => { /* non-blocking -- a missed ping shouldn't disrupt the drive */ });
        },
        (err) => {
          // non-blocking -- a driver's location failing to share never stops
          // the drive, but the banner below at least tells them why dispatch
          // can't see them, instead of silently doing nothing.
          setLocationStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
        },
        { enableHighAccuracy: false, maximumAge: 30000, timeout: 20000 }
      );
    };

    writePing(); // first ping immediately, don't wait 5 minutes for the first one
    const intervalId = setInterval(writePing, 300000); // then every 5 min
    return () => clearInterval(intervalId);
  }, [load?.id, load?.status, nativeTrackingEnabled]);

  const handleStatusUpdate = async (newStatus) => {
    await client.entities.Load.update(load.id, { status: newStatus });
    toast.success(`Load status updated to ${newStatus}`);
    loadData();
  };

  const handleFuelAdvance = async ({ amount, notes, bol_url }) => {
    const grossRate = computeLoadGross(load);
    const maxAdvance = grossRate * 0.4;
    if (amount > maxAdvance) {
      toast.error(`Amount exceeds the maximum fuel advance of $${maxAdvance.toFixed(2)} (40% of gross).`, { duration: 5000 });
      return;
    }
    setRequestingAdvance(true);
    await client.entities.FuelAdvance.create({
      load_id: load.id,
      load_number: load.load_number,
      driver_email: user.email,
      // Prefer the DriverProfile's own full_name (set during onboarding)
      // over user.full_name, which comes from Supabase Auth's
      // user_metadata and is frequently never set -- it was silently
      // falling back to the driver's email as their displayed identity
      // everywhere this record's driver_name gets read back (Payroll,
      // Financials, admin approval modals, reports).
      driver_name: driverProfile?.full_name || user.full_name || user.email,
      amount,
      status: 'Pending',
      notes: notes || '',
      bol_url: bol_url || '',
      advance_type: 'fuel',
    });
    toast.success('Fuel advance requested. Pending admin approval.');
    setShowAdvanceModal(false);
    setRequestingAdvance(false);
    loadData();
  };

  const handlePersonalAdvance = async ({ amount, notes }) => {
    setRequestingPersonalAdvance(true);
    try {
      await client.entities.FuelAdvance.create({
        load_id: null,
        driver_email: user.email,
        driver_name: driverProfile?.full_name || user.full_name || user.email,
        amount,
        status: 'Pending',
        notes: notes || '',
        advance_type: 'personal',
      });
      toast.success('Personal advance requested. Pending admin approval.');
      setShowPersonalAdvanceModal(false);
    } catch (e) {
      // Most likely the $200/week server-side cap (see the fuel_advances
      // weekly-cap trigger) rejected this insert.
      toast.error(e?.message || 'Could not submit personal advance request.');
    } finally {
      setRequestingPersonalAdvance(false);
    }
  };

  const handleEbolUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    toast.info('Uploading eBOL document...');
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    await client.entities.Load.update(load.id, { ebol_url: file_url });
    toast.success('eBOL uploaded! Now click "Mark as Delivered" to complete the load.');
    setUploading(false);
    loadData();
  };

  const handleMarkDelivered = async () => {
    // Guards against a double-click (or a slow connection making the first
    // click look like it didn't register) firing this twice before `load`
    // re-renders out of 'In Transit' -- without it, a second click creates
    // a second Settlement and a second IFTAMileageLog for the same load.
    if (deliveringLoad) return;
    setDeliveringLoad(true);
    try {
      await markLoadDelivered();
    } finally {
      setDeliveringLoad(false);
    }
  };

  const markLoadDelivered = async () => {
    const approvedAdvances = advances.filter(a => a.status === 'Approved');
    const totalAdvances = approvedAdvances.reduce((sum, a) => sum + a.amount, 0);
    // Fetched fresh here (not reused from the LoadReceiptUpload-reported
    // documentedTotal state) for the same reason CompanySettings is
    // re-fetched below -- this is the actual money-computation moment, so
    // it needs whatever the driver has uploaded up to this exact second,
    // not a snapshot from whenever that child component last rendered.
    const loadExpenses = await client.entities.Expense.filter({ load_id: load.id });
    const documentedNow = loadExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    // A driver who documents (with receipts) where the advance money went
    // isn't charged for it -- only the undocumented remainder is deducted.
    // Receipts beyond the advance amount aren't reimbursed here (that's a
    // separate expense-reimbursement conversation); they just can't push
    // this below $0.
    const unaccountedAdvance = Math.max(0, totalAdvances - documentedNow);
    // Fetched fresh here (rather than reusing the companySettings loaded
    // on mount) since this is the actual financial calculation moment --
    // the driver may have had this page open a while, and Accounting's
    // configured percentages should be as current as possible at the
    // point a real charge is computed.
    const settings = await client.entities.CompanySettings.list();
    const carrierSetting = settings.find(s => s.key === 'carrier_take_percent');
    // `parseFloat(x) || 15` couldn't tell "not configured" from "set to
    // 0%" -- a carrier who deliberately zeroed Carrier Take would still
    // get charged 15%.
    const carrierPercent = numOrDefault(carrierSetting?.value, 15);
    // Dispatch Fee % was hardcoded to 3 with no way for Accounting to
    // change it -- now configurable from Pay & Deductions, same as
    // Carrier Take %, defaulting to 3 for carriers that haven't set one.
    const dispatchSetting = settings.find(s => s.key === 'dispatch_fee_percent');
    const dispatchFeePercent = numOrDefault(dispatchSetting?.value, 3);
    const dispatchPercent = driverProfile?.dispatch_service ? dispatchFeePercent : 0;
    const grossRate = computeLoadGross(load);
    // Lumper/late fees are driver-payout deductions only -- never part of
    // what's billed to the broker (see computeLoadGross's own comment).
    const lumperFee = load.lumper_fee || 0;
    const lateFee = load.late_fee || 0;

    // Company Driver pay isn't a revenue split with the carrier -- it's a
    // flat rate set in DriverFormModal (Pay Rate section), either $/mile
    // or a % of the load's gross. Falls back to the standard
    // gross-minus-carrier-take formula below whenever that rate hasn't
    // actually been configured (rate === 0), so an existing company
    // driver's pay doesn't drop to $0 the moment this ships.
    const hasCompanyPayRate = driverProfile?.driver_type === 'Company Driver' && (
      (driverProfile?.pay_type === 'percentage' && driverProfile?.pay_percentage > 0) ||
      (driverProfile?.pay_type === 'mileage' && driverProfile?.pay_per_mile > 0)
    );

    let carrierAmount, dispatchAmount, netPay, carrierPercentUsed, dispatchPercentUsed;
    if (hasCompanyPayRate) {
      const payBasis = driverProfile.pay_type === 'percentage'
        ? grossRate * (driverProfile.pay_percentage / 100)
        : (load.miles || 0) * driverProfile.pay_per_mile;
      dispatchAmount = 0; // dispatch_service is O/O-only, already forced off for company drivers
      dispatchPercentUsed = 0;
      netPay = payBasis - unaccountedAdvance - lumperFee - lateFee;
      // Keep carrier_take_amount/percent meaning "what the carrier kept"
      // so every report/modal reading those fields (SettlementReviewModal,
      // CarrierPayReport, PLReport, etc.) still reconciles correctly
      // without needing its own Company Driver branch.
      carrierAmount = grossRate - payBasis;
      carrierPercentUsed = grossRate > 0 ? (carrierAmount / grossRate) * 100 : 0;
    } else {
      carrierAmount = grossRate * (carrierPercent / 100);
      dispatchAmount = grossRate * (dispatchPercent / 100);
      netPay = grossRate - carrierAmount - dispatchAmount - unaccountedAdvance - lumperFee - lateFee;
      carrierPercentUsed = carrierPercent;
      dispatchPercentUsed = dispatchPercent;
    }

    // weekly_insurance/weekly_eld used to be written here from
    // driverProfile.weekly_insurance/weekly_eld -- but those DriverProfile
    // fields have no editing UI anywhere (never actually set by anyone),
    // and every real weekly insurance/ELD deduction shown across the app
    // (Financials, PayrollTab, SettlementReviewModal) already reads live
    // from CompanySettings' insurance_deduction/eld_weekly_cost instead,
    // never from this Settlement snapshot. Dropped rather than "fixed",
    // since nothing reads these two Settlement columns back either --
    // writing them was pure dead weight.
    const settlementData = {
      net_driver_pay: netPay,
      weekly_truck_payment: driverProfile?.weekly_truck_payment || 0,
      weekly_trailer_payment: driverProfile?.weekly_trailer_payment || 0,
      // Miles this load covered -- informational for O/O, and what
      // Financials.jsx's "This Week's Miles" card sums for Company
      // Drivers paid by the mile.
      miles: load.miles || 0,
    };

    const isOwnerOperator = driverProfile?.driver_type === 'Owner Operator';
    const payDueAt = computePayDueAt(isOwnerOperator, new Date());

    await client.entities.Load.update(load.id, { status: 'Delivered' });
    await client.entities.Settlement.create({
      load_id: load.id,
      load_number: load.load_number,
      driver_email: user.email,
      driver_name: driverProfile?.full_name || user.full_name || user.email,
      status: 'Pending',
      gross_revenue: grossRate,
      carrier_take_percent: carrierPercentUsed,
      carrier_take_amount: carrierAmount,
      dispatch_requested: driverProfile?.dispatch_service ?? false,
      dispatch_fee_percent: dispatchPercentUsed,
      dispatch_fee_amount: dispatchAmount,
      // Snapshot of the rate actually used, purely for record-keeping /
      // an admin auditing a settlement later -- nothing reads this back.
      pay_model: hasCompanyPayRate ? driverProfile.pay_type : 'carrier_take',
      // fuel_advance_total keeps its established meaning -- "what actually
      // got deducted from driver pay" -- since Financials, PayrollTab,
      // RecordPaymentModal, and every report already read it that way.
      // The two new fields are purely informational so a settlement can
      // still show the full picture (raw advance vs. what was documented).
      fuel_advance_total: unaccountedAdvance,
      fuel_advance_requested: totalAdvances,
      fuel_advance_documented: documentedNow,
      lumper_fee_deducted: lumperFee,
      late_fee_deducted: lateFee,
      pay_due_at: payDueAt,
      settlement_date: new Date().toISOString().split('T')[0],
      ...settlementData,
    });

    // Invoice creation: auto-created here (status stays 'Delivered', NOT
    // flipped to 'Invoiced') so it shows up in Accounting -> Invoices right
    // away, but a dispatcher must still separately confirm fees/documents
    // and flip the load's status to 'Invoiced' (EditLoadModal) before it's
    // considered final -- EditLoadModal re-syncs this invoice's amount at
    // that point in case fees were edited after delivery. This replaces an
    // earlier silent auto-create that was deliberately removed for having
    // no invoice_number and a different, incomplete gross formula than
    // AddInvoiceModal's manual flow (see PHASE0_DECISIONS.md's P2-6) --
    // this time it shares the exact same computeLoadGross/nextInvoiceNumber
    // helpers as the manual flow, so the two can't drift apart again.
    const allInvoices = await client.entities.Invoice.list('-created_date', 1000);
    const invoiceNumber = nextInvoiceNumber(allInvoices);
    await client.entities.Invoice.create({
      invoice_number: invoiceNumber,
      load_id: load.id,
      load_number: load.load_number,
      customer_name: load.customer_name || '',
      customer_email: load.customer_email || '',
      total_amount: grossRate,
      balance_due: grossRate,
      invoiced_date: new Date().toISOString().split('T')[0],
      status: 'Unpaid',
    });

    // IFTA mileage log — never block delivery if this fails
    const today = new Date();
    const quarter = `Q${Math.ceil((today.getMonth() + 1) / 3)}-${today.getFullYear()}`;
    let stateMileage = [];
    let iftaSource = 'Manual';
    if (load.origin && load.destination) {
      const result = await calcRoute(
        [bestStop(load.pickup_address, load.origin), bestStop(load.dropoff_address, load.destination)],
        { includeStates: true }
      );
      if (!result.error && result.stateMileage?.length > 0) {
        stateMileage = result.stateMileage;
        iftaSource = 'PCMiler';
      }
    }
    const totalIftaMiles = stateMileage.reduce((sum, s) => sum + s.miles, 0);
    await client.entities.IFTAMileageLog.create({
      load_id: load.id,
      load_number: load.load_number,
      driver_email: user.email,
      origin: load.origin,
      destination: load.destination,
      trip_date: today.toISOString().split('T')[0],
      quarter,
      year: today.getFullYear(),
      state_mileage: JSON.stringify(stateMileage),
      total_miles: totalIftaMiles,
      source: iftaSource,
    });

    toast.success('Load marked as Delivered. Settlement created!');
    loadData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // Pending approval -- same gate as Load Board. A driver who isn't
  // approved yet can't have a real active load, so this was previously
  // just falling through to "No Active Load" below, which reads as the
  // app being broken rather than "you're not approved yet".
  if (driverProfile?.approval_status !== 'Approved') {
    return <PendingApprovalNotice status={driverProfile?.approval_status} />;
  }

  if (!load) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="glass-card p-12 text-center">
          <Truck className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-space text-xl font-semibold text-foreground mb-2">No Active Load</h2>
          <p className="text-muted-foreground mb-6">You don't have a load booked yet. Head to the Load Board to find freight.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-xl font-medium text-sm hover:bg-primary/90 transition-colors">
            Browse Loads <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const stepIndex = statusSteps.indexOf(load.status);

  return (
    <div className="space-y-4 md:space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="font-space text-2xl font-bold text-foreground">Active Load</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your current freight assignment</p>
      </div>

      {/* Status Progress */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-semibold text-foreground">Load #{load.load_number}</h2>
          <span className={cn("text-xs font-semibold px-3 py-1 rounded-full border", statusColors[load.status])}>
            {load.status}
          </span>
        </div>
        <div className="flex items-center">
          {statusSteps.map((step, idx) => (
            <div key={step} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-2 min-w-[4rem]">
                <div className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all",
                  idx <= stepIndex
                    ? "bg-primary border-primary text-primary-foreground"
                    : "bg-muted/60 border-border text-muted-foreground"
                )}>
                  {idx < stepIndex ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                </div>
                <span className={cn(
                  "text-xs font-medium text-center leading-tight",
                  idx <= stepIndex ? "text-foreground" : "text-muted-foreground"
                )}>{step}</span>
              </div>
              {idx < statusSteps.length - 1 && (
                <div className={cn("flex-1 h-0.5 mb-5 mx-2", idx < stepIndex ? "bg-primary" : "bg-white/10")} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Background location opt-in -- native app builds only, before the
          driver has enabled tracking. Starting the watcher is also what
          triggers the OS's "Always Allow" location prompt, so this
          explanation has to come first rather than requesting it
          automatically. */}
      {load.status === 'In Transit' && isNativePlatform() && !nativeTrackingEnabled && (
        <div className="glass-card p-5 border border-blue-500/30 bg-blue-500/5 flex items-start gap-3">
          <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">Share your location with dispatch</p>
            <p className="text-xs text-muted-foreground mt-1">
              Turning this on lets dispatch and your carrier see this load's progress on the map, even while your
              phone is locked. You'll be asked to allow location access — choose "Always Allow" so tracking keeps
              working in the background.
            </p>
            <Button onClick={() => setNativeTrackingEnabled(true)} className="mt-3 bg-blue-600 hover:bg-blue-700 text-sm">
              <MapPin className="w-4 h-4 mr-2" /> Enable Location Tracking
            </Button>
          </div>
        </div>
      )}

      {/* Live status -- covers the web/PWA build always, and the native
          build once tracking has been enabled above. */}
      {load.status === 'In Transit' && (isNativePlatform() ? nativeTrackingEnabled : true) && locationStatus !== 'active' && (
        <div className={cn(
          "flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm",
          locationStatus === 'idle'
            ? "bg-muted/40 border-border text-muted-foreground"
            : "bg-amber-500/10 border-amber-500/30 text-amber-400"
        )}>
          <MapPin className="w-4 h-4 flex-shrink-0" />
          {locationStatus === 'idle' && 'Getting your location…'}
          {locationStatus === 'denied' && "Location sharing is off — enable it in your browser's site settings so dispatch can see your live position."}
          {locationStatus === 'unavailable' && "Location temporarily unavailable — we'll keep trying."}
          {locationStatus === 'unsupported' && "This browser doesn't support location sharing."}
        </div>
      )}

      {load.status === 'In Transit' && locationStatus === 'active' && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border bg-green-500/10 border-green-500/30 text-green-400 text-sm">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
          Sharing location
        </div>
      )}

      {/* Load Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {/* Route Details — Dispatch Sheet */}
        <div className="glass-card p-5 space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Dispatch Sheet</h3>

          {/* Pickup */}
          <div className="rounded-xl bg-muted/40 border border-border overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/60 border-b border-border">
              <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
              <p className="text-xs font-bold text-green-400 uppercase tracking-wider">Pickup (PU)</p>
            </div>
            <div className="p-3 space-y-2">
              <div>
                <p className="text-xs text-muted-foreground">City</p>
                <p className="text-sm font-semibold text-foreground">{load.origin}</p>
              </div>
              {load.pickup_address && (
                <div>
                  <p className="text-xs text-muted-foreground">Address</p>
                  <p className="text-sm text-foreground">{load.pickup_address}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {load.pickup_date && (
                  <div>
                    <p className="text-xs text-muted-foreground">Date</p>
                    <p className="text-sm font-semibold text-green-300">{load.pickup_date}</p>
                  </div>
                )}
                {load.pickup_time_window && (
                  <div>
                    <p className="text-xs text-muted-foreground">Time Window</p>
                    <p className="text-sm text-foreground">{load.pickup_time_window}</p>
                  </div>
                )}
              </div>
              {load.po_pickup_number && (
                <div>
                  <p className="text-xs text-muted-foreground">PO / Pickup #</p>
                  <p className="text-sm font-mono font-semibold text-foreground">{load.po_pickup_number}</p>
                </div>
              )}
            </div>
          </div>

          {/* Additional Stops */}
          {load.additional_stops && (() => {
            let stops = [];
            try { stops = JSON.parse(load.additional_stops); } catch { stops = []; }
            if (!stops.length) return null;
            return (
              <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border-b border-amber-500/20">
                  <div className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                  <p className="text-xs font-bold text-amber-400 uppercase tracking-wider">Additional Stops</p>
                </div>
                <div className="divide-y divide-amber-500/10">
                  {stops.map((stop, i) => (
                    <div key={i} className="p-3 space-y-2">
                      <p className="text-xs font-bold text-amber-400">Stop {i + 1}{stop.company ? ` — ${stop.company}` : ''}</p>
                      {stop.address && (
                        <div>
                          <p className="text-xs text-muted-foreground">Address</p>
                          <p className="text-sm text-foreground">{stop.address}</p>
                        </div>
                      )}
                      {stop.po_number && (
                        <div>
                          <p className="text-xs text-muted-foreground">PO #</p>
                          <p className="text-sm font-mono font-semibold text-foreground">{stop.po_number}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Delivery */}
          <div className="rounded-xl bg-muted/40 border border-border overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/60 border-b border-border">
              <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              <p className="text-xs font-bold text-primary uppercase tracking-wider">Delivery (DO)</p>
            </div>
            <div className="p-3 space-y-2">
              <div>
                <p className="text-xs text-muted-foreground">City</p>
                <p className="text-sm font-semibold text-foreground">{load.destination}</p>
              </div>
              {load.dropoff_address && (
                <div>
                  <p className="text-xs text-muted-foreground">Address</p>
                  <p className="text-sm text-foreground">{load.dropoff_address}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {load.delivery_date && (
                  <div>
                    <p className="text-xs text-muted-foreground">Date</p>
                    <p className="text-sm font-semibold text-primary">{load.delivery_date}</p>
                  </div>
                )}
                {load.dropoff_time_window && (
                  <div>
                    <p className="text-xs text-muted-foreground">Time Window</p>
                    <p className="text-sm text-foreground">{load.dropoff_time_window}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Freight Specs */}
          <div className="border-t border-border pt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {load.miles && <><span className="text-muted-foreground">Miles</span><span className="font-semibold text-foreground text-right">{load.miles} mi</span></>}
            {load.equipment_required && <><span className="text-muted-foreground">Equipment</span><span className="font-semibold text-foreground text-right">{load.equipment_required}</span></>}
            {load.trailer_size && <><span className="text-muted-foreground">Trailer</span><span className="font-semibold text-foreground text-right">{load.trailer_size}</span></>}
            {load.commodity && <><span className="text-muted-foreground">Commodity</span><span className="font-semibold text-foreground text-right">{load.commodity}</span></>}
            {load.weight && <><span className="text-muted-foreground">Weight</span><span className="font-semibold text-foreground text-right">{load.weight}</span></>}
            {load.pallet_case_count && <><span className="text-muted-foreground">Pallets/Cases</span><span className="font-semibold text-foreground text-right">{load.pallet_case_count}</span></>}
            {load.temperature && <><span className="text-muted-foreground">Temperature</span><span className="font-semibold text-foreground text-right">{load.temperature}</span></>}
            {load.hazmat && (
              <div className="col-span-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 mt-1">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-sm font-semibold text-red-400">HAZMAT LOAD</span>
              </div>
            )}
          </div>

          {load.load_requirements && (
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 leading-relaxed">
              <span className="font-semibold">Requirements: </span>{load.load_requirements}
            </div>
          )}
          {load.special_notes && (
            <div className="text-xs text-muted-foreground bg-muted/40 border border-border rounded-lg px-3 py-2 leading-relaxed">
              <span className="font-semibold text-foreground">Notes: </span>{load.special_notes}
            </div>
          )}

          {/* Documents */}
          {(load.ratecon_url || load.ebol_url) && (
            <div className="border-t border-border pt-3 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Documents</p>
              {load.ratecon_url && (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-green-400">Rate Confirmation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={load.ratecon_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-green-400 hover:text-green-300 transition-colors flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> View
                    </a>
                    <a href={load.ratecon_url} download target="_blank" rel="noopener noreferrer"
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                      ↓ Download
                    </a>
                  </div>
                </div>
              )}
              {load.ebol_url && (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-blue-400">eBOL (Bill of Lading)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={load.ebol_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> View
                    </a>
                    <a href={load.ebol_url} download target="_blank" rel="noopener noreferrer"
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                      ↓ Download
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Financials — Rate Confirmation Style */}
        <div className="glass-card p-5">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Load Breakdown</h3>
          {(() => {
            const lineHaul = load.line_haul_rate || 0;
            const accessorial = load.accessorial_pay || 0;
            // Was `lineHaul + accessorial || load.rate` -- missed every
            // other accessorial computeLoadGross accounts for (fuel
            // surcharge, detention, stop-off, tarp, layover, additional
            // fees), so this preview understated gross vs. the real
            // Settlement markLoadDelivered creates below, which has
            // always used computeLoadGross.
            const gross = computeLoadGross(load);
            // Fuel surcharge/detention/stop-off/tarp/layover/extra fees --
            // computeLoadGross includes these in gross but this preview
            // only itemizes Line Haul and Accessorials, so show the rest
            // as one bucket rather than let Gross Rate not add up to the
            // line items shown above it.
            const otherFees = gross - lineHaul - accessorial;
            // Mirrors markLoadDelivered's actual Settlement math below --
            // previously hardcoded to 15%/3% regardless of what
            // Accounting had configured in Pay & Deductions, so this
            // preview could show a driver a different number than what
            // they were actually charged at delivery.
            const carrierSetting = companySettings.find(s => s.key === 'carrier_take_percent');
            const carrierPercent = numOrDefault(carrierSetting?.value, 15);
            const dispatchSetting = companySettings.find(s => s.key === 'dispatch_fee_percent');
            const dispatchFeePercent = numOrDefault(dispatchSetting?.value, 3);
            const approvedAdvances = advances.filter(a => a.status === 'Approved').reduce((s, a) => s + a.amount, 0);
            const pendingAdvances = advances.filter(a => a.status === 'Pending').reduce((s, a) => s + a.amount, 0);
            // Lumper/late fees are real driver-payout deductions in
            // markLoadDelivered's settlementData but were missing here
            // entirely -- this preview overstated net pay by their full
            // amount whenever either was set on the load.
            const lumperFee = load.lumper_fee || 0;
            const lateFee = load.late_fee || 0;
            // Documented receipts (fuel/toll/scale/repair) offset the
            // advance dollar-for-dollar, capped at the advance itself --
            // mirrors markLoadDelivered's unaccountedAdvance math exactly.
            const documentedCapped = Math.min(documentedTotal, approvedAdvances);
            const unaccountedAdvance = Math.max(0, approvedAdvances - documentedTotal);

            // Same hasCompanyPayRate branch as markLoadDelivered -- a
            // Company Driver with a configured $/mile or % rate isn't
            // splitting revenue with the carrier at all, so this preview
            // needs the same fork or it shows a number that disagrees
            // with what they're actually paid at delivery.
            const hasCompanyPayRate = driverProfile?.driver_type === 'Company Driver' && (
              (driverProfile?.pay_type === 'percentage' && driverProfile?.pay_percentage > 0) ||
              (driverProfile?.pay_type === 'mileage' && driverProfile?.pay_per_mile > 0)
            );
            let carrierFee, dispatchFee, carrierFeeLabel;
            if (hasCompanyPayRate) {
              const payBasis = driverProfile.pay_type === 'percentage'
                ? gross * (driverProfile.pay_percentage / 100)
                : (load.miles || 0) * driverProfile.pay_per_mile;
              carrierFee = gross - payBasis;
              dispatchFee = 0;
              carrierFeeLabel = driverProfile.pay_type === 'percentage'
                ? `Retained by carrier (you're paid ${driverProfile.pay_percentage}% of gross)`
                : `Retained by carrier (you're paid $${driverProfile.pay_per_mile.toFixed(2)}/mi × ${load.miles || 0} mi)`;
            } else {
              carrierFee = gross * (carrierPercent / 100);
              dispatchFee = driverProfile?.dispatch_service ? gross * (dispatchFeePercent / 100) : 0;
              carrierFeeLabel = `Carrier Fee (${carrierPercent}%)`;
            }
            // Insurance & ELD are weekly — deducted once at payroll, NOT per load
            const totalDeductions = carrierFee + dispatchFee + unaccountedAdvance + lumperFee + lateFee;
            const netPay = gross - totalDeductions;
            return (
              <div className="space-y-2 text-sm">
                {/* Rate breakdown */}
                <div className="space-y-1 pb-2 border-b border-border">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Line Haul</span>
                    <span className="text-foreground">${lineHaul.toLocaleString()}</span>
                  </div>
                  {accessorial > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Accessorials</span>
                      <span className="text-foreground">+ ${accessorial.toFixed(2)}</span>
                    </div>
                  )}
                  {otherFees > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Other Fees (fuel, detention, etc.)</span>
                      <span className="text-foreground">+ ${otherFees.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold">
                    <span className="text-foreground">Gross Rate</span>
                    <span className="text-primary text-base">${gross.toLocaleString()}</span>
                  </div>
                </div>
                {/* Per-load deductions only */}
                <div className="space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{carrierFeeLabel}</span>
                    <span className="text-red-400">− ${carrierFee.toFixed(2)}</span>
                  </div>
                  {driverProfile?.dispatch_service && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Dispatch Fee ({dispatchFeePercent}%)</span>
                      <span className="text-red-400">− ${dispatchFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Approved Fuel Advance</span>
                    <span className={approvedAdvances > 0 ? "text-red-400" : "text-muted-foreground"}>
                      {approvedAdvances > 0 ? `− $${approvedAdvances.toFixed(2)}` : '$0.00'}
                    </span>
                  </div>
                  {documentedCapped > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Receipts Documented</span>
                      <span className="text-green-400">+ ${documentedCapped.toFixed(2)}</span>
                    </div>
                  )}
                  {pendingAdvances > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-amber-400/70">Pending Advance (not deducted)</span>
                      <span className="text-amber-400/70">${pendingAdvances.toFixed(2)}</span>
                    </div>
                  )}
                  {lumperFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Lumper Fee</span>
                      <span className="text-red-400">− ${lumperFee.toFixed(2)}</span>
                    </div>
                  )}
                  {lateFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Late Fee</span>
                      <span className="text-red-400">− ${lateFee.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div className="border-t border-border pt-2 flex justify-between text-xs">
                  <span className="text-muted-foreground">Total Load Deductions</span>
                  <span className="text-red-400">− ${totalDeductions.toFixed(2)}</span>
                </div>
                <div className="rounded-xl bg-green-500/10 border border-green-500/20 px-3 py-2.5 flex justify-between items-center">
                  <span className="font-semibold text-foreground">Est. Net Pay (this load)</span>
                  <span className="font-bold text-green-400 text-lg">${netPay.toFixed(2)}</span>
                </div>

              </div>
            );
          })()}
        </div>
      </div>

      {/* Actions */}
      {load.status !== 'Delivered' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {/* Status Update */}
          <div className="glass-card p-5">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Update Status</h3>
            {load.status === 'Booked' && (
              <Button onClick={() => handleStatusUpdate('In Transit')} className="w-full bg-blue-600 hover:bg-blue-700">
                <Truck className="w-4 h-4 mr-2" /> Mark In Transit
              </Button>
            )}
            {load.status === 'In Transit' && (
              <div className="space-y-2">
                {load.ebol_url ? (
                  <Button onClick={handleMarkDelivered} disabled={deliveringLoad} className="w-full bg-green-600 hover:bg-green-700">
                    <CheckCircle className="w-4 h-4 mr-2" /> {deliveringLoad ? 'Marking Delivered…' : 'Mark as Delivered'}
                  </Button>
                ) : (
                  <p className="text-sm text-muted-foreground text-center">Upload a signed eBOL below to enable delivery confirmation.</p>
                )}
              </div>
            )}
          </div>

          {/* Fuel Advance */}
          <div className="glass-card p-5">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Fuel Advance</h3>
            {advances.length > 0 && (
              <div className="mb-3 space-y-1">
                {advances.map(adv => (
                  <div key={adv.id} className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">${adv.amount}</span>
                    <span className={cn(
                      "font-semibold px-2 py-0.5 rounded-full border",
                      adv.status === 'Approved' ? 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30' :
                      adv.status === 'Denied' ? 'text-red-700 dark:text-red-400 bg-red-500/15 dark:bg-red-500/10 border-red-500/40 dark:border-red-500/30' :
                      'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30'
                    )}>{adv.status}</span>
                  </div>
                ))}
              </div>
            )}
            {(() => {
              const hasPending = advances.some(a => a.status === 'Pending');
              if (hasPending) {
                return (
                  <Button disabled className="w-full" variant="outline">
                    <Fuel className="w-4 h-4 mr-2" /> Advance Requested
                  </Button>
                );
              }
              return (
                <Button onClick={() => setShowAdvanceModal(true)} variant="outline" className="w-full border-border hover:bg-white/5">
                  <Fuel className="w-4 h-4 mr-2" /> Request Fuel Advance
                </Button>
              );
            })()}
            {(() => {
              const hasPendingPersonal = personalAdvances.some(a => a.status === 'Pending');
              if (hasPendingPersonal) {
                return (
                  <Button disabled className="w-full mt-2" variant="outline">
                    <DollarSign className="w-4 h-4 mr-2" /> Personal Advance Requested
                  </Button>
                );
              }
              return (
                <Button onClick={() => setShowPersonalAdvanceModal(true)} variant="outline" className="w-full mt-2 border-border hover:bg-white/5">
                  <DollarSign className="w-4 h-4 mr-2" /> Request Personal Advance
                </Button>
              );
            })()}
          </div>
        </div>
      )}

      {/* Load Expenses / Receipts */}
      {load.status !== 'Delivered' && (
        <LoadReceiptUpload load={load} user={user} onReceiptsChange={setDocumentedTotal} />
      )}

      {/* eBOL Upload */}
      {load.status === 'In Transit' && (
        <div className="glass-card p-6">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Submit eBOL (Bill of Lading)</h3>
          <label className={cn(
            "flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed rounded-xl cursor-pointer transition-all",
            uploading ? "border-primary/50 bg-primary/5" : "border-border hover:border-primary/50 hover:bg-white/5"
          )}>
            <input type="file" className="hidden" onChange={handleEbolUpload} accept=".pdf,.jpg,.jpeg,.png" disabled={uploading} />
            <Upload className={cn("w-8 h-8", uploading ? "text-primary animate-bounce" : "text-muted-foreground")} />
            <div className="text-center">
              <p className="font-medium text-foreground">{uploading ? 'Uploading...' : 'Upload eBOL Document'}</p>
              <p className="text-xs text-muted-foreground mt-1">PDF, JPG or PNG — Upload to complete delivery & trigger settlement</p>
            </div>
          </label>
        </div>
      )}

      {/* Dispatch Chat */}
      <LoadChat load={load} />

      {/* Fuel Advance Modal */}
      {showAdvanceModal && (
        <FuelAdvanceModal
          load={load}
          user={user}
          submitting={requestingAdvance}
          onClose={() => setShowAdvanceModal(false)}
          onSubmit={handleFuelAdvance}
        />
      )}

      {/* Personal Advance Modal -- same component, different cap/rules */}
      {showPersonalAdvanceModal && (
        <FuelAdvanceModal
          advanceType="personal"
          load={load}
          user={user}
          weeklyPersonalAdvanceTotal={personalAdvances
            .filter(a => a.status === 'Approved' && isSameCalendarWeek(a.approved_date))
            .reduce((sum, a) => sum + a.amount, 0)}
          submitting={requestingPersonalAdvance}
          onClose={() => setShowPersonalAdvanceModal(false)}
          onSubmit={handlePersonalAdvance}
        />
      )}

      {/* Delivered State */}
      {load.status === 'Delivered' && (
        <div className="glass-card border-green-500/30 p-6 text-center">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <h3 className="font-space font-bold text-lg text-foreground mb-1">Delivery Complete!</h3>
          <p className="text-muted-foreground text-sm mb-4">Your eBOL has been submitted and your settlement is ready.</p>
          <a href="/financials" className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium text-sm transition-colors">
            View Settlement <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      )}
    </div>
  );
}